import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "./store";
import { getOrgId } from "./orgHelpers";
import { PurchaseService } from "../services/PurchaseService";
import { DepositsService } from "../services/DepositService";
import { DepositsMonthly } from "../components/chart-bar/chatbar";
import { formatGetTodayDate } from "../utils/format";
import { Deposit, DepositType } from "../models/Depoist";

interface DepositState {
    monthly: Array<any>;
    list: Array<any> | null;
    creditsList: Array<any> | null;
    depositsByDate: Array<any>;
    depositsByUser: Array<Deposit>;
    listDepoistsOrganization: Array<Deposit> | null,
    addDepositModalStatus: boolean
}

const initialState: DepositState = {
    monthly: [],
    list: null,
    creditsList: null,
    depositsByDate: [],
    depositsByUser: [],
    listDepoistsOrganization: null,
    addDepositModalStatus: false
};

const depositsSlice = createSlice({
    name: 'deposits',
    initialState,
    reducers: {
        setDepositsMonthly : (state, action: PayloadAction<Array<any>>) => {
            state.monthly = action.payload;
        },
        setDeposits : (state, action: PayloadAction<Array<any>>) => {
            state.list = action.payload;
        },
        setDepositsByDate: (state, action: PayloadAction<Array<any>>) => {
            state.depositsByDate = action.payload;
        },
        setDepoistsByUser: (state, action: PayloadAction<Array<Deposit>>) => {
            state.depositsByUser = action.payload;
        },
        setListDeposits: (state, action: PayloadAction<Array<Deposit>>) => {
            state.listDepoistsOrganization = action.payload;
        },
        setListCredits: (state, action: PayloadAction<Array<any>|null>) => {
            state.creditsList = action.payload;
        },
        setAddDepoositModalStatus: (state, action: PayloadAction<boolean>) => {
            state.addDepositModalStatus = action.payload;
        },
    }
  });
  
export const {
    setDepositsMonthly, 
    setDeposits, 
    setDepositsByDate, 
    setDepoistsByUser,
    setListDeposits,
    setAddDepoositModalStatus,
    setListCredits
} = depositsSlice.actions;

// monthly
export const fetchDepositMonthly = (id:number | null): AppThunk<Promise<void>> => async dispatch => {
    try {
        const response : Array<any> = await DepositsService.getMonthly(id);
        response.forEach( (monthlyEntry:DepositsMonthly) => {
            monthlyEntry.month = monthlyEntry.month.split("-").reverse().join("-")
        });
        dispatch(setDepositsMonthly(response));
    } catch (error) {
        console.error("Error fetching deposits:", error);
    }
};

// montly
export const fetchDeposits = (id:number): AppThunk<Promise<void>> => async dispatch => {
    try {
        const response : Array<any> = await DepositsService.getMonthly(id);
        dispatch(setDeposits(response));
    } catch (error) {
        console.error("Error fetching deposits:", error);
    }
}

// by date
export const fetchDepositsByDateAndOrganization = (id:number | null): AppThunk<Promise<void>> => async dispatch => {
    const today = formatGetTodayDate();
    try {
        const response : Array<any> = await DepositsService.getDepositsByDate(id,today);
        const filtered = response.filter( (deposit:any) => {
            return deposit.status === 'CONFIRMED' || deposit.status === 'COMPLETED' 
        });
        dispatch(setDepositsByDate(filtered));
    } catch (error) {
        console.error("Error fetching deposits:", error);
    }
}

// by user
export const fetchUserDeposits = (id:number) : AppThunk<Promise<void>> => async dispatch => {
    try {
        const deposits : any = await DepositsService.findDepositsByUser(id)
        if (!deposits) throw new Error('error getting deposits by user');
        dispatch(setDeposits(deposits));
    } catch (e) {
        console.error(e);
    }
    
}

export const fetchCreditsByUserId = (id:number) : AppThunk<Promise<void>> => async dispatch => {
    try {
        const credits : any = await DepositsService.getCreditsByUserId(id);
        // `creditsList` comeca em null e a tela usa isso como "carregando".
        // Sem cair para [], uma resposta vazia ou um erro deixariam o skeleton
        // girando para sempre.
        dispatch(setListCredits(Array.isArray(credits) ? credits : []));
    } catch (e) {
        console.error('fetchCreditsByUserId:', e);
        dispatch(setListCredits([]));
    }
}

/// credits by organization
export const fetchCreditsByOrganization = (filters:any) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    try {
        dispatch(setListCredits(null))
        const state = getState();
        const orgId = getOrgId(state);
        const credits : Array<any> = await DepositsService.getCreditsByOrganization(orgId, filters);
        if (!credits) throw new Error('error getting credits by organization');
        dispatch(setListCredits(credits));
    } catch (e) {
        console.error(e);
    }
}

/// by organization
export const fetchDepositsByOrganization = (id:number) : AppThunk<Promise<void>> => async dispatch => {
    try {
        const depoistsResponse : Array<Deposit> = await DepositsService.getAllDepositsByOrganization(id);
        if (!depoistsResponse) throw new Error('error gettinf deposits by organization');
        dispatch(setDeposits(depoistsResponse));
    } catch (e) {
        console.error(e);
    }
}

/// Admin
export const fetchAllDeposits = () : AppThunk<Promise<void>> => async dispatch => {
    try {
        const depoistsResponse : Array<Deposit> = await DepositsService.getAllDeposits();
        if (!depoistsResponse) throw new Error('error gettinf all deposits');
        dispatch(setDeposits(depoistsResponse));
    } catch (e) {
        console.error(e);
    }
}

// filter
export const fetchDepositsByType = (category:string) : AppThunk<Promise<void>> => async (dispatch,getState) => {
    const state = getState();

    const { id : userId } = state.user.userInfo;
    const orgId = getOrgId(state);

    if (category === DepositType.USER)          dispatch(fetchUserDeposits(userId))
    if (category === DepositType.OWNER)         dispatch(fetchDepositsByOrganization(orgId))
    if (category === DepositType.SUPPLIER)      dispatch(fetchAllDeposits())
    if (category === DepositType.DEPOSITOR)     dispatch(fetchAllDeposits())
    if (category === DepositType.ALL)           dispatch(fetchAllDeposits())

}

export default depositsSlice.reducer;